import { useCallback, useState } from "react";
import * as qz from "qz-tray";
import { getPrinterCertificate, signContent } from "@/apis/tms/printer";

const DEFAULT_HOST = localStorage.getItem("wms-qz-host") ?? "localhost";
// const DEFAULT_PORT = 8181;
let qzInitDone = false;
let connectingPromise: Promise<void> | null = null;
let connected: boolean = false;

async function initQzSecurity() {
  if (qzInitDone) return;
  if (!qz) throw new Error("qz-tray.js not loaded");
  qz.security.setCertificatePromise(
    (resolve: (v: string) => void, reject: (e: any) => void) => {
      getPrinterCertificate()
        .then(resolve)
        .catch((e) => reject(new Error("Failed to load certificate: " + e)));
    }
  );
  qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise((toSign: string) => {
    return (resolve: (sig: string) => void, reject: (e: any) => void) => {
      signContent(toSign)
        .then(resolve)
        .catch((e) => reject(new Error("Failed to sign content: " + e)));
    };
  });
  qzInitDone = true;
}

export async function getQz() {
  await initQzSecurity();
  return qz;
}

export async function connectQz(options?: { host?: string; port?: number }) {
  const qz = await getQz();
  if (qz.websocket.isActive()) return qz;
  if (connectingPromise == null) {
    const host = options?.host ?? DEFAULT_HOST;
    // const port = options?.port ?? DEFAULT_PORT;
    connectingPromise = qz.websocket
      .connect({
        host,
        usingSecure: false,
        retries: 0,
      })
      .then(() => {
        connected = true;
      })
      .catch((err) => {
        console.error(err);
        connected = false;
      })
      .finally(() => {
        connectingPromise = null;
      });
  }
  await connectingPromise;
  return qz;
}

export async function disconnectQz() {
  if (!connected) return Promise.resolve();
  connected = false;
  const qz = await getQz();
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
  }
}

export async function listPrinters() {
  const qz = await connectQz();
  if (!qz.websocket.isActive()) {
    return Promise.resolve([]);
  }
  const printers: string[] | string = await qz.printers.find();
  return Array.isArray(printers) ? printers : [printers];
}

export async function printRaw(
  printer: string,
  data: any | any[],
  opts?: Record<string, any>
) {
  const qz = await connectQz();
  const dataArray = Array.isArray(data) ? data : [data];
  const cfg = qz.configs.create(printer, opts);
  await qz.print(cfg, dataArray);
}

export function useQzTray(host?: string, port?: number) {
  const [connected, setConnected] = useState(false);
  const [printers, setPrinters] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const connect = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await connectQz({ host, port });
      setConnected(true);
    } catch (e) {
      setError(e);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [host, port]);

  const refreshPrinters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ps = await listPrinters();
      setPrinters(ps);
      return ps;
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await disconnectQz();
    } finally {
      setConnected(false);
    }
  }, []);

  const printBlob = useCallback(async (printer: string, pdf: Blob) => {
    setLoading(true);
    setError(null);
    try {
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          const result = reader.result as string;
          const base64 = result.split(",")[1];

          printRaw(
            printer,
            {
              type: "pdf", // 告诉 QZ 这是一个 PDF
              format: "base64", // 可省略，有些版本只看 data 是 base64
              data: base64,
            },
            {
              size: { width: 4, height: 6 },
              orientation: "portrait",
              units: "in",
            }
          )
            .then(resolve)
            .catch(reject);
        };

        reader.onerror = reject;
        reader.readAsDataURL(pdf);
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    connected,
    printers,
    loading,
    error,
    connect,
    disconnect,
    refreshPrinters,
    printBlob,
  };
}
